import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Eye, EyeOff } from 'lucide-react';
import type { IntegrationKind, IntegrationConnection } from '../api/integrations.js';
import { getIntegrationMeta, type FieldDef } from '../lib/integrationMeta.js';
import { useConnectIntegration, useTestIntegration, useUpdateIntegration } from '../hooks/useIntegrations.js';

// We'll import UI components inline via relative path since @hts/ui isn't built yet
// These are lightweight inline versions for the console to avoid circular deps

interface ConnectWizardProps {
  kind: IntegrationKind;
  existingConnection?: IntegrationConnection;
  /** Edit mode: PATCH the existing connection instead of POSTing a new one. */
  editMode?: boolean;
  onClose: () => void;
  onSuccess?: (connection: IntegrationConnection) => void;
}

type WizardStep = 'setup' | 'test' | 'done';

export function ConnectWizard({ kind, existingConnection, editMode = false, onClose, onSuccess }: ConnectWizardProps) {
  const meta = getIntegrationMeta(kind);
  const [step, setStep] = useState<WizardStep>('setup');
  const [config, setConfig] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of meta.configFields) {
      init[f.key] = existingConnection?.config[f.key] ?? '';
    }
    return init;
  });
  const [secret, setSecret] = useState('');
  const [displayName, setDisplayName] = useState(existingConnection?.displayName ?? meta.label);
  const [showSecret, setShowSecret] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newConnection, setNewConnection] = useState<IntegrationConnection | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; detail?: string; notImplemented?: boolean } | null>(null);

  // Editing a healthy connection just saves; fixing an error/pending one
  // re-runs the live test so its status can recover.
  const testAfterSave = !editMode || existingConnection?.status !== 'connected';

  const connectMutation = useConnectIntegration();
  const updateMutation = useUpdateIntegration();
  const testMutation = useTestIntegration();

  function validate() {
    const errs: Record<string, string> = {};
    if (!displayName.trim()) errs.displayName = 'Display name is required';
    for (const f of meta.configFields) {
      if (f.required && !config[f.key]?.trim()) {
        errs[f.key] = `${f.label} is required`;
      }
    }
    if (meta.secretField.required && !secret.trim() && !existingConnection) {
      errs[meta.secretField.key] = `${meta.secretField.label} is required`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleConnect() {
    if (!validate()) return;
    try {
      const conn = editMode
        ? await updateMutation.mutateAsync({ id: existingConnection!.id, input: { displayName, config, secret } })
        : await connectMutation.mutateAsync({ kind, displayName, config, secret });
      setNewConnection(conn);
      // In edit mode the connection already exists (and may not be live-testable);
      // a save is complete once the values are persisted.
      setStep(testAfterSave ? 'test' : 'done');
    } catch {
      // Error handled by mutation
    }
  }

  async function handleTest() {
    if (!newConnection) return;
    try {
      const result = await testMutation.mutateAsync(newConnection.id);
      setTestResult(result);
      if (result.ok) {
        setTimeout(() => setStep('done'), 500);
      } else if (result.notImplemented) {
        // Credentials were saved; this provider has no live test yet.
        setTimeout(() => setStep('done'), 800);
      }
    } catch {
      setTestResult({ ok: false, message: 'Test request failed' });
    }
  }

  function handleDone() {
    if (newConnection) onSuccess?.(newConnection);
    onClose();
  }

  const isConnecting = editMode ? updateMutation.isPending : connectMutation.isPending;
  const isTesting = testMutation.isPending;
  const mutationError = (editMode ? updateMutation.error : connectMutation.error) as Error | null;

  return (
    <div className="flex flex-col h-full">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {(['setup', 'test', 'done'] as WizardStep[]).map((s, i) => {
          const idx = ['setup', 'test', 'done'].indexOf(step);
          const sIdx = i;
          const isComplete = sIdx < idx;
          const isCurrent = s === step;
          return (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${
                isCurrent ? 'text-blue-600' : isComplete ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  isCurrent ? 'bg-blue-600 text-white' :
                  isComplete ? 'bg-green-600 text-white' :
                  'border-2 border-gray-300 text-gray-400'
                }`}>
                  {isComplete ? '✓' : i + 1}
                </div>
                <span className="hidden sm:inline">{s === 'setup' ? 'Configure' : s === 'test' ? 'Test' : 'Done'}</span>
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 ${isComplete ? 'bg-green-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Setup step */}
      {step === 'setup' && (
        <div className="flex flex-col flex-1 overflow-y-auto gap-5">
          {/* Hint */}
          {!editMode && (
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-2">
              How to get your credentials
            </p>
            {meta.setupHint}
          </div>
          )}

          {/* Display name */}
          <Field label="Display Name" error={errors.displayName} required>
            <input
              className={inputCls(!!errors.displayName)}
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder={meta.label}
            />
          </Field>

          {/* Config fields */}
          {meta.configFields.map(f => (
            <Field key={f.key} label={f.label} hint={f.hint} error={errors[f.key]} required={f.required}>
              <input
                className={inputCls(!!errors[f.key])}
                type={f.type === 'password' ? 'password' : 'text'}
                value={config[f.key] ?? ''}
                onChange={e => setConfig(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
              />
            </Field>
          ))}

          {/* Secret field */}
          <Field
            label={meta.secretField.label}
            hint={existingConnection ? 'Leave blank to keep existing secret' : meta.secretField.hint}
            error={errors[meta.secretField.key]}
            required={meta.secretField.required && !existingConnection}
          >
            <div className="relative">
              <input
                className={inputCls(!!errors[meta.secretField.key]) + ' pr-10'}
                type={showSecret ? 'text' : 'password'}
                value={secret}
                onChange={e => setSecret(e.target.value)}
                placeholder={existingConnection ? '••••••••' : meta.secretField.placeholder}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowSecret(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showSecret ? 'Hide secret' : 'Show secret'}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>

          {mutationError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-400">
              {mutationError.message}
            </div>
          )}
        </div>
      )}

      {/* Test step (connect / fix flow; editing a healthy connection goes straight to done) */}
      {step === 'test' && (
        <div className="flex flex-col flex-1 items-center justify-center gap-6 py-8">
          <div className={`flex h-16 w-16 items-center justify-center rounded-2xl text-2xl ${meta.color}`}>
            {meta.icon}
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {newConnection?.displayName} {editMode ? 'saved' : 'connected'}
            </p>
            <p className="text-sm text-gray-500">Run a test to verify the credentials work.</p>
          </div>

          {testResult && (
            <div className={`w-full rounded-lg border p-4 ${
              testResult.ok || testResult.notImplemented
                ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {testResult.ok
                  ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                  : <AlertCircle className="h-4 w-4 text-red-600" />
                }
                <span className={`text-sm font-medium ${testResult.ok ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                  {testResult.message}
                </span>
              </div>
              {testResult.detail && (
                <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">{testResult.detail}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Done step */}
      {step === 'done' && (
        <div className="flex flex-col flex-1 items-center justify-center gap-4 py-8">
          <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-green-100 dark:bg-green-900/30">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">All set!</p>
            <p className="text-sm text-gray-500">
              {testResult?.ok
                ? `${newConnection?.displayName ?? 'Integration'} is connected and verified.`
                : testResult?.notImplemented
                  ? 'Credentials saved. A live test is not available for this integration yet.'
                  : 'Changes saved.'}
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          {step === 'done' ? 'Close' : 'Cancel'}
        </button>

        {step === 'setup' && (
          <button
            onClick={handleConnect}
            disabled={isConnecting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isConnecting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isConnecting ? (editMode ? 'Saving…' : 'Connecting…') : editMode ? 'Save changes' : 'Connect'}
          </button>
        )}

        {step === 'test' && !testResult && (
          <button
            onClick={handleTest}
            disabled={isTesting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isTesting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isTesting ? 'Testing…' : 'Test Connection'}
          </button>
        )}

        {step === 'test' && testResult && !testResult.ok && (
          <button
            onClick={handleTest}
            disabled={isTesting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isTesting && <Loader2 className="h-4 w-4 animate-spin" />}
            Retry Test
          </button>
        )}

        {step === 'done' && (
          <button
            onClick={handleDone}
            className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Finish
          </button>
        )}
      </div>
    </div>
  );
}

// Minimal local helpers to avoid circular dep with @hts/ui during dev
function inputCls(error: boolean) {
  return `flex h-9 w-full rounded-md border px-3 py-1 text-sm transition-colors bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    error ? 'border-red-500' : 'border-gray-300 dark:border-gray-700 hover:border-gray-400'
  }`;
}

function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {label}
        {required && <span className="ml-1 text-red-500" aria-hidden>*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
      {!error && hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
